/**
 * AdminWelcomePackLeads
 * ────────────────────────────────────────────────────────────────────────
 * Admin page (/admin/welcome-pack-leads) listing every visitor who unlocked
 * the homepage Welcome Pack, plus a KPI strip and role/intent breakdowns.
 * Backed by GET /api/admin/welcome-pack/{leads,summary}.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Download, Lock } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import apiClient from '../../api/client';

const ROLE_LABELS = {
  parent: 'Parent / Guardian',
  teen: 'Teen',
  educator: 'Educator',
  school: 'School / District',
  other: 'Other',
};

const AdminWelcomePackLeads = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (roleFilter) params.set('role', roleFilter);
      const [summaryData, leadsData] = await Promise.all([
        apiClient.request('/api/admin/welcome-pack/summary'),
        apiClient.request(`/api/admin/welcome-pack/leads?${params.toString()}`),
      ]);
      setSummary(summaryData);
      setLeads(leadsData.leads || []);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const header = ['email', 'name', 'role', 'first_intent', 'last_intent', 'unlock_count', 'first_seen_at', 'last_seen_at'];
    const rows = leads.map((l) => header.map((k) => {
      const v = l[k];
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    }).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `welcome-pack-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout pageTitle="Welcome Pack Leads">
      <div data-testid="admin-welcome-pack-leads">
        {/* Page-local action bar (Refresh + CSV) — AdminLayout supplies the
            outer chrome, so this strip only holds the page-specific buttons. */}
        <div className="bg-white border border-gray-200 rounded-2xl px-5 py-3 mb-5 flex items-center justify-between flex-wrap gap-3" data-testid="welcome-pack-leads-actions">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Back to admin home"
              data-testid="welcome-pack-leads-back"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FFF5EA' }}>
              <Lock size={16} style={{ color: '#F5831F' }} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#F5831F' }}>Lead Capture</p>
              <h2 className="text-sm font-bold text-[#1A1209]">Welcome Pack Leads</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Refresh"
              data-testid="welcome-pack-leads-refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={downloadCsv}
              className="px-3 py-2 text-xs font-semibold text-[#7B4FD8] border border-[#7B4FD8] rounded-lg hover:bg-purple-50 inline-flex items-center gap-1.5"
              data-testid="welcome-pack-leads-csv"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* KPI strip */}
          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="welcome-pack-leads-kpi">
            <KpiCard label="Total leads" value={summary?.total ?? '—'} accent="#F5831F" />
            <KpiCard label="Last 7 days" value={summary?.last_7d ?? '—'} accent="#7B4FD8" />
            <KpiCard label="Parents" value={summary?.by_role?.parent ?? 0} accent="#F5831F" />
            <KpiCard label="Schools" value={summary?.by_role?.school ?? 0} accent="#7B4FD8" />
          </section>

          {/* Role filter */}
          <section className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-gray-600">Filter:</span>
            {[
              { value: '', label: 'All' },
              { value: 'parent', label: 'Parents' },
              { value: 'teen', label: 'Teens' },
              { value: 'educator', label: 'Educators' },
              { value: 'school', label: 'Schools' },
              { value: 'other', label: 'Other' },
            ].map((opt) => (
              <button
                key={opt.value || 'all'}
                onClick={() => setRoleFilter(opt.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
                  roleFilter === opt.value ? 'bg-[#1A1209] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`welcome-pack-leads-filter-${opt.value || 'all'}`}
              >
                {opt.label}
              </button>
            ))}
          </section>

          {/* Leads table */}
          <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {loading ? (
              <div className="px-6 py-12 text-center text-sm text-gray-500" data-testid="welcome-pack-leads-loading">Loading…</div>
            ) : error ? (
              <div className="px-6 py-12 text-center text-sm text-red-600" data-testid="welcome-pack-leads-error">{error}</div>
            ) : leads.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-gray-500" data-testid="welcome-pack-leads-empty">
                No leads yet. Once visitors unlock the Welcome Pack on the homepage, they&apos;ll appear here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="welcome-pack-leads-table">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Role</th>
                      <th className="px-4 py-3 text-left">Last action</th>
                      <th className="px-4 py-3 text-right">Unlocks</th>
                      <th className="px-4 py-3 text-left">First seen</th>
                      <th className="px-4 py-3 text-left">Last seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leads.map((l) => (
                      <tr key={l.lead_id || l.email} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-[#1A1209]">{l.email}</td>
                        <td className="px-4 py-3 text-gray-700">{l.name}</td>
                        <td className="px-4 py-3 text-gray-700">{ROLE_LABELS[l.role] || l.role}</td>
                        <td className="px-4 py-3 text-gray-700 capitalize">{l.last_intent || '—'}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{l.unlock_count}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(l.first_seen_at)}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(l.last_seen_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
};

const KpiCard = ({ label, value, accent }) => (
  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
    <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent }}>{label}</p>
    <p className="text-2xl font-bold text-[#1A1209] mt-1">{value}</p>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export default AdminWelcomePackLeads;
